import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import PageHero from '../components/PageHero';
import { api } from '../lib/api';
import { ExternalLink, Download, Plus, BookOpen, Newspaper, FileBarChart2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';

// Live yekuv.org yayınları (fallback when DB is empty)
const DEFAULT_PUBLICATIONS = [
  {
    id: 'yekuv-30-yil',
    title: 'YEKÜV 30. Yıl Kitabı',
    year: '2022',
    type: 'kitap',
    cover: 'https://yekuv.org/wp-content/uploads/2023/08/30yilkitap.png',
    pdf_url: 'https://yekuv.org/wp-content/uploads/2023/08/YEKUV-30.-YIL-KITAP-2022-Y.pdf',
    description: 'Vakfımızın 30. yılı için hazırlanan anı kitabı.',
  },
  {
    id: 'yekuv-25-yil',
    title: 'YEKÜV 25. Yıl Kitabı',
    year: '2017',
    type: 'kitap',
    cover: 'https://yekuv.org/wp-content/uploads/2023/08/25yilkitaap.png',
    pdf_url: 'https://yekuv.org/wp-content/uploads/2023/07/yekuv-25.-YIL-KITABI-PDF.pdf',
    description: 'Vakfımızın 25. yılı anı kitabı.',
  },
  {
    id: 'baris-kulturu',
    title: 'Barış Kültürü',
    year: '2020',
    type: 'kitap',
    cover: 'https://yekuv.org/wp-content/uploads/2023/08/Ekran-Resmi-2023-08-31-14.02.19-795x1024.png',
    pdf_url: 'https://yekuv.org/wp-content/uploads/2023/07/bariskulutur.pdf',
    description: 'Barış Kültürü — YEKÜV yayını.',
  },
  {
    id: 'eb-calistay-raporu',
    title: 'Eğitim Bursu Çalıştay Raporu',
    year: '2026',
    type: 'rapor',
    cover: 'https://yekuv.org/wp-content/uploads/2026/04/Ekran-goruntusu-2026-04-13-162832.png',
    pdf_url: 'https://yekuv.org/wp-content/uploads/2026/04/EB_YEKUV_Calistay_Raporu_en-son-1-1.pdf',
    description: 'EB YEKÜV Çalıştay Raporu — 2026.',
  },
];

const TABS = [
  { key: 'all', label: 'Tümü', icon: BookOpen },
  { key: 'kitap', label: 'Kitaplar', icon: BookOpen },
  { key: 'dergi', label: 'Dergiler', icon: Newspaper },
  { key: 'rapor', label: 'Raporlar', icon: FileBarChart2 },
];

const HERO_BY_TYPE = {
  all: {
    kicker: 'Yayınlarımız',
    title: <>Kitaplarımız, <span className="italic text-coral-200">dergilerimiz</span>, raporlarımız.</>,
    text: 'Kapak görsellerine tıklayarak PDF olarak okuyabilir ya da indirebilirsiniz.',
  },
  kitap: {
    kicker: 'Kitaplarımız',
    title: <>YEKÜV <span className="italic text-coral-200">kitapları</span>.</>,
    text: 'Anı kitapları, tematik yayınlar ve araştırmalarımız — hepsini PDF olarak buradan okuyabilirsiniz.',
  },
  dergi: {
    kicker: 'Dergilerimiz',
    title: <>YEKÜV <span className="italic text-coral-200">dergileri</span>.</>,
    text: 'Süreli yayınlarımız, sayı sayı arşivimiz — kapağa tıklayıp PDF olarak okuyun.',
  },
  rapor: {
    kicker: 'Raporlarımız',
    title: <>YEKÜV <span className="italic text-coral-200">raporları</span>.</>,
    text: 'Çalıştay raporları, faaliyet ve etki değerlendirmeleri — hepsi PDF olarak buradan okunabilir.',
  },
};

export default function Publications() {
  useSEO('publications');
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState(DEFAULT_PUBLICATIONS);
  const [fromDb, setFromDb] = useState(false);

  const rawType = (searchParams.get('type') || 'all').toLowerCase();
  const currentType = TABS.some(t => t.key === rawType) ? rawType : 'all';
  const hero = HERO_BY_TYPE[currentType];

  useEffect(() => {
    api.publicContent('publications').then(list => {
      if (Array.isArray(list) && list.length > 0) {
        setItems(list.map(it => ({ id: it.id, ...(it.data || {}) })));
        setFromDb(true);
      }
    }).catch(() => {});
  }, []);

  const switchType = (t) => {
    if (t === 'all') setSearchParams({});
    else setSearchParams({ type: t });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filtered = currentType === 'all'
    ? items
    : items.filter(p => (p.type || 'kitap').toLowerCase() === currentType);

  const counts = TABS.reduce((acc, t) => {
    acc[t.key] = t.key === 'all' ? items.length : items.filter(p => (p.type || 'kitap').toLowerCase() === t.key).length;
    return acc;
  }, {});

  return (
    <>
      <PageHero slug={`publications_${currentType}`}
        kicker={hero.kicker}
        title={hero.title}
        text={hero.text}
      />

      {/* Tabs bar */}
      <div className="bg-cream border-b border-ink-100 sticky top-16 z-30" data-testid="publications-tabs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = currentType === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => switchType(t.key)}
                  data-testid={`pub-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 px-5 h-14 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${active ? 'border-ink-600 text-ink-600' : 'border-transparent text-ink-400 hover:text-ink-600'}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${active ? 'bg-ink-600 text-cream' : 'bg-ink-50 text-ink-400'}`}>{counts[t.key] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          {/* Admin add button — visible only in edit mode */}
          <div className="yekuv-admin-only mb-8 text-right" style={{ display: 'none' }}>
            <Link to="/admin/publications" data-testid="add-publication" className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold hover:bg-ink-500">
              <Plus className="w-4 h-4" /> Yeni Yayın Ekle {fromDb ? '' : '(Şu an varsayılan liste gösteriliyor)'}
            </Link>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10" data-testid="publications-grid">
              {filtered.map((p, i) => (
                <EditRegion key={p.id || i} label="Yayını Düzenle" to="/admin/publications">
                  <PublicationCard p={p} />
                </EditRegion>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white border border-ink-100 rounded-3xl">
              <div className="text-ink-400 text-lg">Bu kategoride henüz yayın yok.</div>
              <Link to="/admin/publications" className="mt-4 inline-flex items-center gap-2 text-ink-600 font-semibold text-sm hover:text-ink-500">
                <Plus className="w-4 h-4" /> Admin panelinden ekle
              </Link>
            </div>
          )}

          <div className="mt-16 rounded-3xl bg-ink-600 text-cream p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <InlineText settingKey="publications_cta_kicker" fallback="Basılı Yayınlar" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
              <InlineText settingKey="publications_cta_title" fallback="Kütüphanemizi ziyaret edin." as="h3"
                multiline className="font-display text-3xl md:text-4xl mt-3 leading-tight" />
              <InlineText settingKey="publications_cta_text" fallback="Basılı yayınlarımıza YEKÜV Şişli merkezindeki kütüphanemizden ulaşabilir, satış geliri bursiyerlerimize eğitim desteği olarak kullanılan koleksiyona katkı sunabilirsiniz." as="p"
                multiline className="mt-4 text-cream/75" />
            </div>
            <div>
              <Link to="/iletisim" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-12 font-semibold hover:bg-gold-400 transition-colors">İletişime Geç <ExternalLink className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function PublicationCard({ p }) {
  const hasCover = !!p.cover;
  const hasPdf = !!p.pdf_url;
  const typeLabel = { kitap: 'Kitap', dergi: 'Dergi', rapor: 'Rapor' }[(p.type || '').toLowerCase()] || null;
  return (
    <div className="group" data-testid={`publication-${p.id || 'x'}`}>
      <a
        href={p.pdf_url || '#'}
        target={hasPdf ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="block relative rounded-2xl overflow-hidden bg-white border border-ink-100 shadow-md shadow-ink-900/5 hover:shadow-2xl hover:shadow-ink-900/20 hover:-translate-y-1 transition-all aspect-[3/4]"
        onClick={(e) => { if (!hasPdf) e.preventDefault(); }}
      >
        {hasCover ? (
          <img src={p.cover} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full grid place-items-center bg-cream-200 text-ink-400 text-xs px-4 text-center">Kapak eklenmedi</div>
        )}
        {typeLabel && (
          <span className="absolute top-3 left-3 text-[9px] font-bold uppercase tracking-widest bg-white/90 backdrop-blur text-ink-600 rounded-full px-2.5 py-1">{typeLabel}</span>
        )}
        {hasPdf && (
          <div className="absolute inset-0 bg-ink-900/0 group-hover:bg-ink-900/40 transition-colors grid place-items-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-gold text-ink-700 rounded-full px-4 h-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
              <Download className="w-3.5 h-3.5" /> PDF&apos;i Aç
            </span>
          </div>
        )}
      </a>
      <div className="mt-3 text-center">
        <div className="font-display text-base text-ink-600 leading-tight">{p.title}</div>
        {(p.year || p.issue) && (
          <div className="text-[10px] uppercase tracking-widest text-ink-400 mt-1 font-semibold">
            {p.year}{p.issue ? ` · ${p.issue}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
